<template>
  <component :is="component" v-bind="componentProps">
    <v-row>
      <v-col>
        <order-delivery-required
          v-model="order.delivery_time"
          v-model:date="order.delivery_date"
          v-model:pickup="order.customer_pickup"
          v-model:site-contact="order.site_contact"
          :no-hold="noHold"
        />

        <qtm-checkbox
          v-if="showReceive && canReceive"
          v-model="order.mark_items_as_received"
          class="ml-2 mt-6"
        >
          <template v-slot:label>
            <span class="qtm-body text-secondary">
              This order has already been received
            </span>
            <v-tooltip class="text-center" location="top" width="315">
              <template v-slot:activator="{ props: activator }">
                <v-icon v-bind="activator" class="ml-2" color="mid-grey" icon="mdi-information-outline" />
              </template>
              Confirm that the items have been received in full and complete the order.
            </v-tooltip>
          </template>
        </qtm-checkbox>

        <slot name="options-append" />
      </v-col>
    </v-row>

    <v-row v-if="deliveryRequired">
      <v-col>
        <qtm-input-label :label="`${deliveryOrPickup} Contact`">
          <qtm-autocomplete
            v-model="order.site_contact"
            hide-details
            :item-title="teamName"
            :items="jobsiteTeam"
            :loading="loading"
            style="max-width: 300px"
          />
        </qtm-input-label>
      </v-col>
    </v-row>

    <order-delivery-location
      v-model:location="order.location"
      v-model:location-type="order.delivery_location"
      :disabled="!!order.location && lockLocation"
      :error-messages="errors.order.location"
      :inventory="inventory"
      :jobsite="order.jobsite"
      :label="locationLabel"
      :no-inventory="noInventory"
      :require="inventory"
      @update:inventory-location="$emit('update:inventory-location', $event)"
    />

    <v-row v-if="deliveryDateRequired">
      <v-col cols="12" md="6">
        <qtm-input-label :label="`${deliveryOrPickup} Date & Time`" :required="validateDeliveryRequired">
          <order-delivery-date
            v-model="v$.order.delivery_date.$model"
            v-model:delivery-time="order.delivery_time"
            :cutoff="cutoffDate"
            :date-picker-menu-props="datePickerMenuProps"
            :disabled="!deliveryRequired"
            :error-messages="errorMessage || deliveryDateError"
            :pickup="order.customer_pickup"
          />
        </qtm-input-label>
      </v-col>
    </v-row>

    <slot />
  </component>
</template>

<script>
import OrderDeliveryDate from '@/components/orders/order-delivery-date.vue'
import OrderDeliveryLocation from '@/components/orders/order-delivery-location.vue'
import OrderDeliveryRequired from '@/components/orders/order-delivery-required.vue'
import { DateTime } from '@quotetome/materials-api'
import { DELIVERY_DATE_REQUIRED_VALUES, DELIVERY_REQUIRED_VALUES } from '@/constants'
import useValidation from '@/composables/validation'
import { requiredIf } from '@vuelidate/validators'

export default {
  name: 'order-delivery',
  components: { OrderDeliveryDate, OrderDeliveryLocation, OrderDeliveryRequired },
  props: {
    collapsed: {
      type: Boolean,
      default: false
    },
    collapsible: {
      type: Boolean,
      default: true
    },
    cutoffDate: {
      type: Object,
      default: undefined
    },
    datePickerMenuProps: {
      type: Object,
      default: undefined
    },
    errorMessage: {
      type: String,
      default: undefined
    },
    flat: {
      type: Boolean,
      default: false
    },
    inventory: {
      type: Boolean,
      default: false
    },
    lockLocation: {
      type: Boolean,
      default: false
    },
    noHold: {
      type: Boolean,
      default: false
    },
    noInventory: {
      type: Boolean,
      default: false
    },
    order: {
      type: Object,
      required: true,
    },
    showReceive: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Fulfillment Details',
    },
    validateDeliveryRequired: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['update:inventory-location', 'update:team'],
  setup() {
    const { errors, isValid, v$ } = useValidation()

    return { errors, isValid, v$ }
  },
  validations: {
    order: {
      delivery_date: {
        required: requiredIf(function () {
          return this.validateDeliveryRequired && (
            this.order.delivery_time === '-' || DELIVERY_DATE_REQUIRED_VALUES.includes(this.order.delivery_time)
          )
        }),
      },
      location: {
        required: requiredIf(function () {
          return this.inventory
        }),
      },
    }
  },
  data() {
    return {
      jobsiteTeam: [],
      loading: false,
    }
  },
  computed: {
    component() {
      return this.flat ? 'v-col' : 'qtm-content-block'
    },
    componentProps() {
      if (this.flat) {
        return undefined
      }

      return {
        collapsed: this.collapsed,
        collapsible: this.collapsible,
        title: this.title,
      }
    },
    deliveryDateError() {
      return this.v$.$dirty && this.v$.$invalid ? 'A delivery date is required' : undefined
    },
    deliveryLocationOptions() {
      const options = [
        {
          label: 'Site',
          value: 'jobsite',
        },
        {
          label: 'Office',
          value: 'office',
        },
      ]

      if (this.order.jobsite && this.order.jobsite.locations.length) {
        options.push({
          label: 'Other',
          value: 'other',
        })
      }

      return options
    },

    deliveryOrPickup() {
      return this.order.customer_pickup ? 'Pick Up' : 'Delivery'
    },

    deliveryDateRequired() {
      return this.deliveryRequired && this.order.delivery_time !== 'hold'
    },

    deliveryRequired() {
      return this.order.delivery_time === '-' || DELIVERY_REQUIRED_VALUES.includes(this.order.delivery_time)
    },
    canReceive() {
      const today = new DateTime()
        .withHour(24)
        .withMinute(0)
        .withSecond(0)
        .withMillisecond(0)

      return !this.order.delivery_date || this.order.delivery_date.isBefore(today)
    },
    locationLabel() {
      if (this.inventory) {
        return 'Inventory Location'
      }

      if (this.deliveryRequired) {
        return 'Delivery Address'
      }

      return 'Order Location'
    },
  },
  watch: {
    deliveryRequired() {
      this.order.delivery_required = this.deliveryRequired && !this.order.customer_pickup
    },
    'order.customer_pickup': {
      handler() {
        this.order.delivery_required = this.deliveryRequired && !this.order.customer_pickup
      }
    },
    'order.jobsite': {
      handler() {
        if (!this.order.jobsite?.locations.some(location => location.id === this.order.location)) {
          this.order.location = null
        }
        this.fetchJobsiteTeam()
      }
    },
    canReceive() {
      if (!this.canReceive) {
        this.order.mark_items_as_received = false
      }
    }
  },
  mounted() {
    this.fetchJobsiteTeam()
  },
  methods: {
    async fetchJobsiteTeam() {
      if (!this.order.jobsite && !this.inventory) {
        return
      }

      const params = { inventory: this.inventory || undefined, jobsite: this.order.jobsite?.id }

      this.loading = true
      try {
        const team = await this.$api.v1.users.list(params)
        this.$emit('update:team', team)
        this.jobsiteTeam = team
      }
      catch (error) {
        this.$error.report(error)
      }
      this.loading = false
    },
    teamName(user) {
      let entry = user

      if (typeof entry === 'number') {
        entry = this.jobsiteTeam.find(u => u.id === entry) || entry
      }

      if (entry?.first_name) {
        return `${entry.first_name} ${entry.last_name}`
      }

      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
